import React from 'react';
import { Idol } from 'types';
import { safeGetItemSizeProperty } from 'utils';
import { generateTradeUrl, hasValidTradeData } from 'utils/tradeUtils';

interface IdolItemProps {
  item: Idol;
  isDraggable?: boolean;
  onDragStart: (item: Idol) => void;
  onRemove: (itemId: string) => void;
  onDuplicate: (item: Idol) => void; // New prop for duplication
  onEdit?: (item: Idol) => void;
  onMouseEnter?: (e: React.MouseEvent<HTMLDivElement>) => void;
  onMouseLeave?: (e: React.MouseEvent<HTMLDivElement>) => void;
}

const IdolItem: React.FC<IdolItemProps> = ({ 
  item, 
  isDraggable = true, 
  onDragStart, 
  onRemove,
  onDuplicate, // New prop
  onEdit,
  onMouseEnter,
  onMouseLeave
}) => {
  // Add defensive checks
  const itemColor = safeGetItemSizeProperty(item, 'color', '#888888');
  const itemWidth = safeGetItemSizeProperty(item, 'width', 1);
  const itemHeight = safeGetItemSizeProperty(item, 'height', 1);
  const itemName = item.name || (item.size?.name ?? "Unknown Idol");
  
  const handleDragStart = (): void => {
    onDragStart(item);
  };

  const handleMouseDown = (action: 'trade' | 'edit' | 'delete' | 'duplicate', e: React.MouseEvent): void => {
    // Middle mouse button is 1
    if (e.button === 1) {
      e.preventDefault();
      
      if (action === 'trade') {
        const tradeUrl = generateTradeUrl(item);
        if (tradeUrl) {
          window.open(tradeUrl, '_blank', 'noopener,noreferrer');
        }
      } else if (action === 'edit' && onEdit) {
        onEdit(item);
      } else if (action === 'delete') {
        onRemove(item.id);
      } else if (action === 'duplicate') {
        onDuplicate(item);
      }
    }
  };

  const handleTradeClick = (e: React.MouseEvent<HTMLButtonElement>): void => {
    e.stopPropagation();
    const tradeUrl = generateTradeUrl(item);
    if (tradeUrl) {
      window.open(tradeUrl, '_blank', 'noopener,noreferrer');
    }
  };
  
  const canTrade = hasValidTradeData(item);
  
  return (
    <div 
      className={`flex items-center gap-2 ${isDraggable ? 'cursor-grab' : ''} bg-gray-700 p-3 rounded hover:bg-gray-600 transition-colors duration-200`}
      draggable={isDraggable}
      onDragStart={isDraggable ? handleDragStart : undefined}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      <div 
        className="border border-gray-600 mr-2 flex-shrink-0"
        style={{ 
          backgroundColor: itemColor,
          width: `${itemWidth * 20}px`,
          height: `${itemHeight * 20}px`
        }}
      />
      <div className="flex flex-col flex-grow">
        <div className="text-sm font-medium text-white">{itemName}</div>
        <div className="text-xs text-blue-400">{itemWidth}x{itemHeight}</div>
      </div>
      <div className="flex items-center ml-auto">
        {canTrade && (
          <button
            className="text-yellow-400 hover:text-yellow-300 mr-2 transition-colors"
            onClick={handleTradeClick}
            onMouseDown={(e) => handleMouseDown('trade', e)}
            title="Search trade site"
            type="button"
            aria-label="Search trade"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
            </svg>
          </button>
        )}
        <button
          className="text-green-400 hover:text-green-300 mr-2 transition-colors"
          onClick={(e: React.MouseEvent<HTMLButtonElement>): void => {
            e.stopPropagation();
            onDuplicate(item);
          }}
          onMouseDown={(e) => handleMouseDown('duplicate', e)}
          type="button"
          aria-label="Duplicate"
          title="Duplicate idol"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
          </svg>
        </button>
        {onEdit && (
          <button
            className="text-blue-400 hover:text-blue-300 mr-2 transition-colors"
            onClick={(e: React.MouseEvent<HTMLButtonElement>): void => {
              e.stopPropagation();
              onEdit(item);
            }}
            onMouseDown={(e) => handleMouseDown('edit', e)}
            type="button"
            aria-label="Edit"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
            </svg>
          </button>
        )}
        <button 
          className="text-red-400 hover:text-red-300 transition-colors"
          onClick={(e: React.MouseEvent<HTMLButtonElement>): void => {
            e.stopPropagation();
            onRemove(item.id);
          }}
          onMouseDown={(e) => handleMouseDown('delete', e)}
          type="button"
          aria-label="Remove"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
          </svg>
        </button>
      </div>
    </div>
  );
};

export default IdolItem;